/**
 * OrganizationList Component
 *
 * Displays a paginated list of partner organizations fetched from the API.
 * Supports loading states, error handling, and pagination.
 *
 * Uses:
 * - useCustomerOrganizations hook for data fetching
 * - useAuthConfig hook for authentication
 */

import React, { useState } from 'react';
import {
  Building2,
  ShieldCheck,
  ShieldAlert,
  Calendar,
  Eye,
  PlusCircle,
  RefreshCw,
  XCircle,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { useCustomerOrganizations, type CustomerOrganization } from '@cfleet/api-client';
import { useAuthConfig } from '@/hooks/useAuthConfig';

interface OrganizationListProps {
  onSelectOrg: (id: string) => void;
  onNewOrder: (id: string) => void;
}

// ─────────────────────────────────────────────────────────────────────────────
// Loading Skeleton
// ─────────────────────────────────────────────────────────────────────────────

const OrganizationListSkeleton: React.FC = () => (
  <div
    data-testid="organization-list-loading"
    className="bg-white"
  >
    <div className="h-14 border-b border-zinc-200 flex items-center justify-between px-6 bg-white">
      <div className="h-4 w-40 bg-zinc-200 rounded animate-pulse" />
      <div className="h-4 w-32 bg-zinc-200 rounded animate-pulse" />
    </div>
    <div>
      <table className="w-full">
        <thead className="sticky top-0 bg-white z-10 border-b border-zinc-200">
          <tr>
            <th className="px-6 py-3">
              <div className="h-3 w-20 bg-zinc-200 rounded animate-pulse" />
            </th>
            <th className="px-6 py-3">
              <div className="h-3 w-16 bg-zinc-200 rounded animate-pulse" />
            </th>
            <th className="px-6 py-3">
              <div className="h-3 w-24 bg-zinc-200 rounded animate-pulse" />
            </th>
            <th className="px-6 py-3">
              <div className="h-3 w-20 bg-zinc-200 rounded animate-pulse" />
            </th>
            <th className="px-6 py-3">
              <div className="h-3 w-16 bg-zinc-200 rounded animate-pulse" />
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-100">
          {[1, 2, 3, 4, 5].map((i) => (
            <tr key={i}>
              <td className="px-6 py-4">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-zinc-200 rounded-lg animate-pulse" />
                  <div className="space-y-2">
                    <div className="h-4 w-32 bg-zinc-200 rounded animate-pulse" />
                    <div className="h-3 w-20 bg-zinc-200 rounded animate-pulse" />
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="h-5 w-16 bg-zinc-200 rounded-full animate-pulse" />
              </td>
              <td className="px-6 py-4">
                <div className="h-4 w-24 bg-zinc-200 rounded animate-pulse" />
              </td>
              <td className="px-6 py-4">
                <div className="h-4 w-28 bg-zinc-200 rounded animate-pulse" />
              </td>
              <td className="px-6 py-4">
                <div className="h-8 w-20 bg-zinc-200 rounded animate-pulse" />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Error State
// ─────────────────────────────────────────────────────────────────────────────

const ErrorState: React.FC<{ onRetry: () => void }> = ({ onRetry }) => (
  <div className="flex flex-col items-center justify-center h-full bg-white p-8">
    <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
      <XCircle className="w-8 h-8 text-red-500" />
    </div>
    <h3 className="text-lg font-semibold text-zinc-900 mb-2">Failed to Load Organizations</h3>
    <p className="text-sm text-zinc-500 mb-4 text-center max-w-sm">
      We couldn't load your partner organizations. Please check your connection and try again.
    </p>
    <button
      onClick={onRetry}
      className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors"
    >
      <RefreshCw size={16} />
      Retry
    </button>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// Main Component
// ─────────────────────────────────────────────────────────────────────────────

export const OrganizationList: React.FC<OrganizationListProps> = ({ onSelectOrg, onNewOrder }) => {
  const authConfig = useAuthConfig();
  const [page, setPage] = useState(1);
  const perPage = 20;

  // Fetch organizations from API
  const { data, isLoading, isError, refetch } = useCustomerOrganizations(
    { page, per_page: perPage },
    { authConfig }
  );

  const organizations = data?.data ?? [];
  const pagination = data?.pagination;
  const summary = data?.summary;

  // Show loading skeleton
  if (isLoading) {
    return <OrganizationListSkeleton />;
  }

  // Show error state
  if (isError) {
    return <ErrorState onRetry={() => refetch()} />;
  }

  return (
    <div className="bg-white">
      {/* Header */}
      <div className="h-14 border-b border-zinc-200 flex items-center justify-between px-6 bg-white sticky top-0 z-20">
        <h2 className="text-sm font-semibold text-zinc-900">Partner Organizations</h2>
        <div className="text-xs text-zinc-500 font-medium">
          Active Partnerships:{' '}
          <span className="text-zinc-900">
            {summary?.total_active ?? organizations.filter((o) => o.status === 'active').length}
          </span>
        </div>
      </div>

      <div className="pb-12">
        {organizations.length === 0 ? (
          /* Empty State */
          <div className="py-20 flex flex-col items-center justify-center text-center">
            <Building2 size={48} className="text-zinc-200 mb-4" />
            <h3 className="text-zinc-900 font-medium">No partners found</h3>
            <p className="text-zinc-500 text-sm">Contact support to link with a carrier.</p>
          </div>
        ) : (
          /* Organization Table */
          <table className="w-full text-left text-sm border-collapse">
            <thead className="sticky top-14 bg-white z-10 border-b border-zinc-200 text-xs uppercase text-zinc-500 font-medium shadow-sm">
              <tr>
                <th className="px-6 py-3 font-semibold">Partner</th>
                <th className="px-6 py-3 font-semibold">Status</th>
                <th className="px-6 py-3 font-semibold">Partner Since</th>
                <th className="px-6 py-3 font-semibold">Auth Level</th>
                <th className="px-6 py-3 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {organizations.map((org) => (
                <tr
                  key={org.id}
                  className={`group hover:bg-zinc-50 transition-colors cursor-default ${org.status === 'disabled' ? 'opacity-60 bg-zinc-50/50' : ''}`}
                >
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div
                        className={`w-9 h-9 rounded-lg flex items-center justify-center border transition-colors
                        ${org.status === 'active' ? 'bg-zinc-100 border-zinc-200 text-zinc-500 group-hover:bg-white group-hover:border-blue-200' : 'bg-zinc-200 border-zinc-300 text-zinc-400'}
                      `}
                      >
                        <Building2 size={18} />
                      </div>
                      <div>
                        <div className="font-semibold text-zinc-900 text-sm">{org.name}</div>
                        <div className="text-[10px] text-zinc-400 font-mono tracking-tighter">
                          {org.id}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wide border
                      ${org.status === 'active' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200'}
                    `}
                    >
                      <div
                        className={`w-1 h-1 rounded-full ${org.status === 'active' ? 'bg-emerald-500' : 'bg-red-500'}`}
                      ></div>
                      {org.status}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2 text-zinc-500 text-xs">
                      <Calendar size={13} />
                      {new Date(org.joined_at).toLocaleDateString(undefined, {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                      })}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1.5 text-[11px] font-medium text-zinc-500">
                      {org.status === 'active' ? (
                        <>
                          <ShieldCheck size={14} className="text-emerald-500" /> Fully Authorized
                        </>
                      ) : (
                        <>
                          <ShieldAlert size={14} className="text-red-400" /> Restricted Access
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      {org.status === 'active' && (
                        <button
                          onClick={() => onNewOrder(org.id)}
                          title="New Order with this Partner"
                          className="p-1.5 rounded-md text-blue-600 hover:bg-blue-50 transition-colors"
                        >
                          <PlusCircle size={16} />
                        </button>
                      )}
                      <button
                        onClick={() => onSelectOrg(org.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 border border-transparent hover:border-zinc-200 transition-all"
                      >
                        <Eye size={14} />
                        Details
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination Controls */}
        {pagination && pagination.total_pages > 1 && (
          <div className="flex items-center justify-center gap-4 py-4 px-6 border-t border-zinc-200 bg-white">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={!pagination.has_prev}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft size={14} />
              Previous
            </button>
            <span className="text-xs text-zinc-500">
              Page {pagination.page} of {pagination.total_pages}
            </span>
            <button
              onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
              disabled={!pagination.has_next}
              className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-zinc-600 hover:text-zinc-900 hover:bg-zinc-100 rounded-md disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
              <ChevronRight size={14} />
            </button>
          </div>
        )}

        {/* Discover Partners CTA */}
        <div className="mt-8 mx-6 bg-zinc-50 border border-zinc-200 border-dashed rounded-xl p-6 flex flex-col md:flex-row items-center justify-between text-center md:text-left gap-4">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center border border-zinc-200 shadow-sm shrink-0">
              <PlusCircle size={20} className="text-zinc-400" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-zinc-900">Partner with more carriers?</h4>
              <p className="text-xs text-zinc-500 max-w-sm mt-0.5">
                Request connection with other delivery providers to expand your fleet reach.
              </p>
            </div>
          </div>
          <button className="whitespace-nowrap px-4 py-2 rounded-lg bg-white border border-zinc-200 text-sm font-medium text-zinc-700 hover:bg-zinc-50 transition-colors shadow-sm">
            Discover Partners
          </button>
        </div>
      </div>
    </div>
  );
};
